import { useState, type KeyboardEvent, type PointerEvent } from 'react';
import { cn } from '@/lib/utils';

/** How far an arrow key moves the handle. */
const KEY_STEP = 32;

/**
 * A drag handle between two panes: drag it, or focus it and press ← or →, to resize the pane beside
 * it. It keeps no width itself: the caller holds the width (`onResize` while dragging), saves it when
 * the drag ends (`onResizeEnd`, usually a layout pref in UI_PREFS) and lays the panes out. Hover and
 * focus show a grip line.
 */
export function PaneResizeHandle({
  width,
  min,
  max,
  grows = 'right',
  onResize,
  onResizeEnd,
  onDraggingChange,
  label,
  className,
}: {
  /** The resized pane's width now, in px. */
  width: number;
  min: number;
  /** The widest it may be, worked out when a drag or key press starts (the handle, to measure its row). */
  max: (handle: HTMLElement) => number;
  /** Which way to drag to make the pane wider: `right` for a pane left of the handle, `left` for one right of it. */
  grows?: 'left' | 'right';
  onResize: (width: number) => void;
  onResizeEnd: (width: number) => void;
  /** While dragging, e.g. to stop an iframe beside it swallowing the pointer. */
  onDraggingChange?: (dragging: boolean) => void;
  /** Says what it resizes, e.g. "Resize document panel". */
  label: string;
  className?: string;
}) {
  const [drag, setDrag] = useState<{ startX: number; startWidth: number; max: number } | null>(null);
  const sign = grows === 'right' ? 1 : -1;
  const clamp = (next: number, upTo: number) => Math.min(Math.max(min, upTo), Math.max(min, Math.round(next)));

  const startDrag = (next: typeof drag) => {
    setDrag(next);
    onDraggingChange?.(next != null);
  };
  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    const upTo = max(event.currentTarget);
    startDrag({ startX: event.clientX, startWidth: Math.min(width, upTo), max: upTo });
  };
  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!drag) return;
    onResize(clamp(drag.startWidth + sign * (event.clientX - drag.startX), drag.max));
  };
  const handlePointerUp = () => {
    if (!drag) return;
    startDrag(null);
    onResizeEnd(width);
  };
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const upTo = max(event.currentTarget);
    const next = clamp(Math.min(width, upTo) + sign * (event.key === 'ArrowRight' ? KEY_STEP : -KEY_STEP), upTo);
    onResize(next);
    onResizeEnd(next);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      aria-valuenow={width}
      aria-valuemin={min}
      tabIndex={0}
      className={cn('group flex w-4 shrink-0 cursor-col-resize touch-none justify-center outline-none', className)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
    >
      <div
        className={cn(
          'my-auto h-12 w-1 rounded-full transition-colors group-hover:bg-border group-focus-visible:bg-ring',
          drag ? 'bg-ring' : 'bg-transparent'
        )}
      />
    </div>
  );
}
